import { useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { useQuery } from '@apollo/react-hooks';
import { usePathParam } from '@atlassian/jira-platform-router-utils';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import {
	type ProjectKey,
	type ProjectId,
	toProjectId,
	toProjectKey,
} from '@atlassian/jira-shared-types';
import type { ServiceQueryResult } from '@atlassian/jira-software-deployments-service-query-types';
import type { IssueType } from '../../common/types';

import { useGiraClient } from '../../common/utils/graphql-clients';
import type {
	jira_cycleTimeReport_projectIssueTypes_Query as ProjectQuery,
	jira_cycleTimeReport_projectIssueTypes_QueryVariables as ProjectQueryVariables,
} from './__generated_apollo__/jira_cycleTimeReport_projectIssueTypes_Query';
import { PROJECT_QUERY } from './gql';
import { transformQueryResult } from './utils';

export const useProjectKey = (): ProjectKey => {
	const [projectKey] = usePathParam('projectKey');
	if (projectKey == null) {
		throw new Error('Project key missing');
	}
	return toProjectKey(projectKey);
};

export const useProjectId = (): ProjectId | null => {
	const { data } = useProjectContext();
	return data === null ? null : toProjectId(`${data.projectId}`);
};

export const useProjectAdmin = (): boolean | null => {
	const { data } = useProjectContext();
	if (data === null) {
		return null;
	}
	const { isProjectAdmin } = data;
	return isProjectAdmin === undefined ? null : isProjectAdmin;
};

export const useProjectSimplified = (): boolean | null => {
	const { data } = useProjectContext();
	return data === null ? null : data.simplified;
};

export const useProjectIssueTypes = (): ServiceQueryResult<IssueType[]> => {
	const projectKey = useProjectKey();
	const giraClient = useGiraClient();
	const queryResult = useQuery<ProjectQuery, ProjectQueryVariables>(PROJECT_QUERY, {
		variables: { projectKey },
		client: giraClient,
	});
	return useMemo(() => transformQueryResult(queryResult), [queryResult]);
};
type IssueTypesLookup = {
	[key: string]: IssueType;
};
export const useIssueTypesLookup = (): IssueTypesLookup => {
	const { data: issueTypes } = useProjectIssueTypes();
	return useMemo(() => {
		if (!issueTypes) {
			throw new Error('project data is undefined but is required for useIssueTypesLookup');
		}
		return keyBy(issueTypes, 'id');
	}, [issueTypes]);
};

type ProjectAnalyticsAttributes = {
	isSimplified: boolean;
	isProjectAdmin: boolean;
	projectId: ProjectId;
} | null;

export const useProjectAnalyticsAttributes = (): ProjectAnalyticsAttributes => {
	const projectId = useProjectId();
	const isProjectAdmin = useProjectAdmin();
	const isProjectSimplified = useProjectSimplified();

	if (projectId === null || isProjectAdmin === null || isProjectSimplified === null) {
		return null;
	}

	return {
		isSimplified: isProjectSimplified,
		isProjectAdmin,
		projectId,
	};
};
